import { Component, NgZone, OnDestroy, Input, AfterViewInit } from "@angular/core";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { IAppState } from "@edp-store/state/app.state";
import { Store, select } from "@ngrx/store";
import { getInvoices } from "@edp-store/selectors/dashboard.selectors";
import moment from "moment";
import am4lang_pt_PT from "@amcharts/amcharts4/lang/pt_PT";
import am4lang_en_EN from "@amcharts/amcharts4/lang/en_US";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { IInvoicesChart } from "@edp-models/invoices.model";
import { ValueAxis } from "@amcharts/amcharts4/charts";
import { LanguageService } from "@edp-services/language.service";
import { LocaleEnum } from "@edp-enums/language.enum";

am4core.options.autoSetClassName = true;
am4core.useTheme(am4themes_animated);
am4core.addLicense("CH120763846");

@Component({
    selector: "edp-chart",
    templateUrl: "./edp-chart.component.html",
    styleUrls: ["./edp-chart.component.scss"]
})
export class EdpChartComponent implements AfterViewInit, OnDestroy {
    private unsubscribe = new Subject();
    private totalAverageRangeValue: number;
    private averageEnergyRangeValue: number;
    private chart: am4charts.XYChart;

    @Input() public fullCard: boolean;

    @Input() set chartId(chartId: string) {
        this.chartName = chartId;
    }

    @Input() set balance(balance: any) {
        this.balanceObject = balance;
        this.setValues();
    }

    public balanceObject: { balance: number; };
    public chartName: string;
    public invoices = [];
    public noInvoices: boolean;
    public loading = true;
    public value: string;
    public decimals: string;
    public showBalance: boolean;


    constructor(
        private zone: NgZone,
        private store: Store<IAppState>,
        private languageService: LanguageService,
        ) {
    }

    public ngAfterViewInit() {
        this.store.pipe(
            takeUntil(this.unsubscribe),
            select(getInvoices)
        ).subscribe((data) => {
            if (data.invoicesChart) {
                if (data.invoicesChart.length !== 0) {
                    this.noInvoices = false;
                    this.invoices = this.formatData(data.invoicesChart);
                    this.totalAverageRangeValue = data.monthlyAverage;
                } else {
                    this.noInvoices = true;
                }
                this.chartConstruction();
                this.loading = false;
            }
        });

        if (this.balanceObject.balance === 0) {
            this.showBalance = false;
        } else {
            this.showBalance = true;
        }
    }

    public setValues() {
        if (this.balanceObject) {
            this.value = ((this.balanceObject.balance) + "").split(".")[0];
            this.decimals = ((this.balanceObject.balance) + "").split(".")[1];
        }
    }

    public chartConstruction() {
        this.zone.runOutsideAngular(() => {
            const chart = am4core.create(this.chartName, am4charts.XYChart);
            chart.hiddenState.properties.opacity = 0; // this creates initial fade-in
            chart.height = am4core.percent(100);
            chart.paddingTop = 20;
            chart.marginLeft = 0;
            chart.paddingLeft = 0;
            // Array with all the data

            const data = this.getData();
            data.reverse();
            chart.data = data;
            this.getLocale(chart);
            const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
            dateAxis.renderer.grid.template.location = 0;
            dateAxis.renderer.minGridDistance = 20;
            dateAxis.periodChangeDateFormats.setKey("month", "MMM");
            dateAxis.renderer.grid.template.disabled = true;
            // color of label's month.
            dateAxis.renderer.labels.template.fill = am4core.color("#455558");

            const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
            let lastValue = 0;
            let minValue = 0;
            chart.data.forEach((item) => {
                if (item.price > lastValue) {
                    lastValue = item.price;
                }
                if (item.price < minValue) {
                    minValue = item.price;
                }
            });

            valueAxis.max = lastValue;
            valueAxis.min = minValue;
            valueAxis.renderer.grid.template.disabled = true;
            // Set up axis title
            valueAxis.layout = "absolute";
            valueAxis.title.text = "€";
            valueAxis.title.rotation = 0;
            valueAxis.title.valign = "top";
            valueAxis.title.paddingLeft = 10;
            valueAxis.title.dy = -20;
            // title color "€"
            valueAxis.title.fill = am4core.color("#7C9599");
            valueAxis.title.fontWeight = "bold";
            valueAxis.renderer.baseGrid.disabled = true;
            // color number of values ex: "30€"
            valueAxis.renderer.labels.template.fill = am4core.color("#7C9599");

            const series = chart.series.push(new am4charts.ColumnSeries());
            // Set Y - Valor medio das faturas
            series.dataFields.valueY = "price";
            series.dataFields.dateX = "date";
            series.columns.template.tooltipText = "{valueY}€";
            series.tooltip.getFillFromObject = false;
            series.tooltip.background.fill = am4core.color("white");
            series.tooltip.label.fill = am4core.color("#225E66");
            series.tooltip.label.fontWeight = "bold";
            series.tooltip.pointerOrientation = "down";
            // Cor
            series.fill = am4core.color("#225E66");
            // // Border
            series.columns.template.strokeOpacity = 0;
            // Column Template
            const columnTemplate = series.columns.template;
            // Width column
            columnTemplate.width = 10;
            columnTemplate.column.cornerRadius(20, 20, 20, 20);
            columnTemplate.configField = "config";
            this.setTotalAverageRange(valueAxis);
            this.setAverageEnergyRange(valueAxis);
            this.chart = chart;
        });
    }

    public getData() {
        if (this.invoices.length !== 0) {
            return this.invoices.slice(0, 6);
        } else {
            return this.getDummyData();
        }
    }

    private getDummyData() {
        return Array.apply(null, Array(6)).map((c, index) => {
            const date = moment().subtract(index, "months");
            return {
                date: date.format("YYYY-MM"),
                price: 50
            };
        });
    }


    getLocale(chart): void {
        if (this.languageService.getLocale() !== LocaleEnum.pt) {
            chart.language.locale = am4lang_en_EN;
            chart.dateFormatter.language.locale = am4lang_en_EN;
        } else {
            chart.language.locale = am4lang_pt_PT;
            chart.dateFormatter.language.locale = am4lang_pt_PT;

        }

    }

    private get creditBarConfig() {
        return {
            strokeOpacity: 1,
            strokeDasharray: 3,
            stroke: am4core.color("#9351c2"),
            strokeWidth: 1.5,
            fillOpacity: 0,
        };
    }

    private formatData(data: IInvoicesChart[]) {
        return [...data].map((item) => {
            if (item.price < 0) {

                return {
                    ...item,
                    price: Math.abs(item.price),
                    config: this.creditBarConfig
                };
            }
            return item;
        });
    }

    private setTotalAverageRange(valueAxis: ValueAxis): void {
        if (Boolean(this.totalAverageRangeValue)) {
            const rangeAT = valueAxis.axisRanges.create();
            rangeAT.id = "AT";
            rangeAT.value = this.totalAverageRangeValue;
            // color line of average
            rangeAT.grid.stroke = am4core.color("#7C9599");
            rangeAT.grid.strokeWidth = 1;
            rangeAT.grid.strokeOpacity = 1;
            rangeAT.label.text = rangeAT.value + " €[/]";
            rangeAT.label.inside = true;
            rangeAT.label.align = "left";
            rangeAT.label.verticalCenter = "bottom";
            // color of average number
            rangeAT.label.fill = am4core.color("#7C9599");
        }
    }

    private setAverageEnergyRange(valueAxis: ValueAxis): void {
        if (Boolean(this.averageEnergyRangeValue)) {
            const rangeAE = valueAxis.axisRanges.create();
            rangeAE.value = this.averageEnergyRangeValue;
            rangeAE.id = "AE";
            rangeAE.grid.stroke = am4core.color("#7C9599");
            rangeAE.grid.strokeWidth = 1;
            rangeAE.grid.strokeOpacity = 1;
            rangeAE.grid.strokeDasharray = "3";
            rangeAE.label.text = rangeAE.value + " €[/]";
            rangeAE.label.inside = true;
            rangeAE.label.align = "left";
            rangeAE.label.verticalCenter = "bottom";
            rangeAE.label.fill = am4core.color("#7C9599");
        }
    }

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.zone.runOutsideAngular(() => {
            if (this.chart) {
                this.chart.dispose();
            }
        });
    }

}
